import React from 'react'

import PropTypes from 'prop-types'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Form } from 'react-bootstrap'
import { useForm, FormProvider } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'

import { setupValidationLocale } from '../utils'
import Field from './Field'
import Radios from './Radios'
import SprOrgSpecificFields from './SprOrgSpecificFields'
import SchNetSpecificFields from './SchNetSpecificFields'
import useTrackingContext from '../../hooks/useTrackingContext'
import { useOrderApiClient } from '../OrderApi'
import useScrolledToElement from '../../hooks/useScrolledToElement'

// NOTE: This has to be set before defining any validation Schema
// (or importing a Component that does)
// otherwise these localizations are not picked up.
setupValidationLocale()

const baseValidationSchema = yup.object({
  first_name: yup.string().required(),
  last_name: yup.string().required(),
  street: yup.string().required(),
  street_number: yup.string().required(),
  zip_code: yup.string().required(),
  city: yup.string().required(),
  country_code: yup.string().required(),
  email: yup.string().email().required(),
  terms_and_conditions: yup.bool().oneOf([true], 'muss akzeptiert werden'),
  media_preference: yup.string().oneOf(
    ['print', 'pdf', 'print_and_pdf'],
    'muss ausgewählt werden'
  )
}).required()

const sprachreisenValidationPartialSchema = yup.object({
  birth_year: yup
    .number()
  // we would get a type error on empty strings
  // instead of the "required" message without this:
    .transform(value => (isNaN(value) ? null : value))
    .min(1901)
    .required(),
  target_group: yup.string().required(),
})

const schueleraustauschValidationPartialSchema = yup.object({
  exchange_country_code: yup.string().required(),
})

const mediaPreferenceLabels = [
  {
    id: 'print_and_pdf',
    name: 'Beides: Post & E-Mail',
  },
  {
    id: 'print',
    name: 'Per Post',
  },
  {
    id: 'pdf',
    name: 'Per E-Mail',
  },
]

const CatalogForm = (props) => {
  const {
    organisation,
    forwarded,
    referrer,
    entrypoint,
    children,
    interestInSchoolPrograms,
    programId,
  } = props
  const tracker = useTrackingContext()
  const client = useOrderApiClient()
  let businessAreaSpecificFields = (<></>)
  let privacyUrl
  let defaultValues = {
    first_name: '',
    last_name: '',
    email: '',
    street: '',
    street_number: '',
    zip_code: '',
    city: '',
    phone: '',
    referrer: referrer,
    forwarded: forwarded,
    entrypoint: entrypoint,
    organisation_slug: organisation.slug,
    country_code: 'DE',
    terms_and_conditions: false,
    media_preference: '',
    program_id: programId,
    interest_in_school_programs: interestInSchoolPrograms,
  }

  const scrolledToElement = useScrolledToElement({})


  let validationSchema

  switch (props.businessArea) {
    case 'schueleraustausch':
      businessAreaSpecificFields = (
        <SchNetSpecificFields
          availableExchangeCountryCodes={organisation.target_countries}
        />
      )
      privacyUrl = `https://www.schueleraustausch.net/pages/datenschutz`
      defaultValues.exchange_country_code = props.exchangeCountryCode
      validationSchema = baseValidationSchema.concat(
        schueleraustauschValidationPartialSchema
      )
      break
    case 'sprachreisen':
      businessAreaSpecificFields = (
        <SprOrgSpecificFields available_target_groups={organisation.target_groups} />
      )
      privacyUrl = 'https://www.sprachreisen.org/static/datenschutz'
      defaultValues.birth_year = ''
      defaultValues.target_group = ''
      validationSchema = baseValidationSchema.concat(
        sprachreisenValidationPartialSchema
      )
      break
  }

  const formMethods = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema)
  })
  const {
    register,
    formState: { errors, isValidating },
    handleSubmit,
    control,
  } = formMethods

  const createOrder = useMutation({
    mutationFn: (values) => {
      // TODO: transform values as described in https://react-hook-form.com/advanced-usage#TransformandParse
      values.birth_year = parseInt(values.birth_year, 10)
      // ...
      if ('phone' in values && values.phone.trim() === '') {
        delete values['phone']
      }

      return client.createOrder(props.businessArea, values)
    },
    onSuccess: (order) => {
      tracker.trackEvent('CatalogOrder', 'Form submitted')
      tracker.trackEcommerce(order.id, [
        {
          id: order.id,
          name: organisation.shortname,
          category: 'CatalogOrder',
          sku: 'Catalog-' + organisation.shortname
        }
      ])

      props.onSuccess(order)
    },
    useErrorBoundary: true,
  })

  // Guard submission while validating or posting to API
  // NOTE: we cannot rely on isSubmitting here due to
  // incompatibility between react-hook-form and react-query
  // see: https://github.com/react-hook-form/react-hook-form/issues/9927
  const cannotSubmit = isValidating || createOrder.isLoading

  // Uncomment console.warn here to log possible validation errors e.g. during tests
  const onSubmit = handleSubmit(createOrder.mutate, /*, console.warn */)

  tracker.trackEvent('CatalogOrder', 'Show Form')

  return(
    <div data-testid='initial-form' style={{ clear: 'both' }} ref={scrolledToElement}>
      {children}
      <FormProvider {...formMethods}>
        <Form
          onSubmit={onSubmit} noValidate
          data-testid='catalog-form'
          className='catalog-order-form row g-3'
        >
          <Form.Group className='field-firstname field-lead col-md-6' controlId='first_name'>
            <Form.Label>Vorname&nbsp;*</Form.Label>
            <Field name='first_name' rules={{ required: true }} />
          </Form.Group>
          <Form.Group className='field-lastname field-lead col-md-6' controlId='last_name'>
            <Form.Label>Nachname&nbsp;*</Form.Label>
            <Field name='last_name' rules={{ required: true }} />
          </Form.Group>
          <Form.Group className='field-street field-lead col-md-6' controlId='street'>
            <Form.Label>Strasse&nbsp;*</Form.Label>
            <Field name='street' rules={{ required: true }} />
          </Form.Group>
          <Form.Group
            className='field-street-number field-lead col-md-6'
            controlId='street_number'
          >
            <Form.Label>Hausnummer&nbsp;*</Form.Label>
            <Field name='street_number' rules={{ required: true }} />
          </Form.Group>
          <Form.Group className='field-zip-code field-lead col-md-2' controlId='zip_code'>
            <Form.Label>Postleitzahl&nbsp;*</Form.Label>
            <Field name='zip_code' rules={{ required: true }} />
          </Form.Group>
          <Form.Group className='field-city field-lead col-md-4' controlId='city'>
            <Form.Label>Stadt&nbsp;*</Form.Label>
            <Field name='city' rules={{ required: true }} />
          </Form.Group>
          <Form.Group className='field-country-code field-lead col-md-6' controlId='country_code'>
            <Form.Label>Land&nbsp;*</Form.Label>
            <Form.Select  {...register('country_code')} >
              <option value='DE'>Deutschland</option>
              <option value='AT'>Österreich</option>
              <option value='CH'>Schweiz</option>
              <option value='NL'>Niederlande</option>
              <option value='BE'>Belgien</option>
              <option value='LI'>Liechtenstein</option>
              <option value='LU'>Luxemburg</option>
            </Form.Select>
            {errors.country_code?.type === 'required' && (
              <Form.Control.Feedback type='invalid'>
                Erforderlich
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group className='field-email field-lead col-md-6' controlId='email'>
            <Form.Label>Email&nbsp;*</Form.Label>
            <Field name='email' />
          </Form.Group>
          <Form.Group className='field-phone field-lead col-md-6' controlId='phone'>
            <Form.Label>Telefon</Form.Label>
            <Field name='phone' />
          </Form.Group>

          {businessAreaSpecificFields}

          <Form.Group>
            <Form.Label>Ich bevorzuge den Katalog *</Form.Label>
            <Radios
              name='media_preference'
              options={mediaPreferenceLabels}
              control={control}
            />
            <Form.Text muted>
              Nicht alle Austauschorganisationen bieten den Katalog in Papier- und PDF-Format an.
            </Form.Text>
          </Form.Group>

          <div className='field-terms-and-conditions field-legal'>
            <Form.Check
              type='checkbox'
              id='tos'
              label='Ich willige ein,'
              {...register('terms_and_conditions', { required: true })}
              isInvalid={errors.terms_and_conditions}
            />
            <p className={`${errors.terms_and_conditions ? 'text-danger' : 'text-muted'} small terms-and-conditions-text`}>
              dass meine Kontaktdaten an die von mir während des
              Bestellprozesses ausgewählten Veranstalter
              weitergeleitet werden. Diese dürfen mich per Post, E-Mail
              und/oder Telefon über ihre Angebote für Werbezwecke
              kontaktieren. Die Einwilligung in die Zusendung kann ich
              jederzeit dem jeweiligen Veranstalter gegenüber
              widerrufen.&nbsp;*
              {errors.terms_and_conditions && (
                <>
                  <br/>
                  <span className='text-danger mt-2 d-inline-block'>
                    Muss akzeptiert werden
                  </span>
                </>
              )}
            </p>
          </div>
          <div className='form-actions'>
            <button type='submit' className='btn btn-primary' disabled={cannotSubmit}>
              Weiter
            </button>
            <button type='button' onClick={props.onCancel} className='btn btn-link'>
              Abbrechen
            </button>
          </div>
          <p className='text-muted mandatory-fields-info'>(*) Pflichtfeld</p>
          <p className='privacy-notice'>Es gilt unsere&nbsp;
            <a
              href={privacyUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='privacy-link'
            >
              Datenschutzerklärung
            </a>
          </p>
        </Form>
      </FormProvider>
    </div>
  )
}

CatalogForm.propTypes = {
  businessArea: PropTypes.oneOf(['sprachreisen', 'schueleraustausch']).isRequired,
  interestInSchoolPrograms: PropTypes.bool.isRequired,
  programId: PropTypes.string,
  organisation: PropTypes.object.isRequired,
  forwarded: PropTypes.string,
  referrer: PropTypes.string.isRequired,
  entrypoint: PropTypes.string.isRequired,
  exchangeCountryCode: PropTypes.string,
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  children: PropTypes.node,
}

export default CatalogForm
